import React, { useEffect, useRef, useState } from "react";
import './styles/formData.css';
import { countries } from './resources/countries';
import { languageMessages } from './traductor/formData'
import { REACT_APP_FLYWIRE_CODE_US, REACT_APP_FLYWIRE_CODE_OTHER,REACT_APP_COUNTRY_DEPLOY, REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION } from "config";
import { resolveRecurrence } from "../services/payment/rules";
import { Dropdown, InputText, Phone } from "@ole/react-library-agents";
import OleCheckbox from "./OleCheckbox";
import { getCityCodeByZone } from "services/common";

interface FormPersonalDataProps {
    objectData: any,
    onClick: Function,
    language: string,
    buttonText: string,
    showRecurrent: boolean,
    isFirstPayment?: boolean,
    showPaymentMethodTitle?: boolean,
    hideButton?: boolean,
    brokerLayout?: boolean,
    zipCodeLabel?: string,
    belowButtonContent?: React.ReactNode,
}

export interface FormPersonalDataRef {
    submitForm: () => void;
}

const FormPersonalData = React.forwardRef<FormPersonalDataRef, FormPersonalDataProps>(
({ objectData, onClick, language, buttonText, showRecurrent, isFirstPayment = false, showPaymentMethodTitle = false, hideButton = false, brokerLayout = false, zipCodeLabel, belowButtonContent }, ref) => {
    const [inputFirstnameValue, setInputFirstnameValue] = useState(isFirstPayment ? objectData.firstName : objectData.payerFirstName);
    const [inputMiddlenameValue, setInputMiddlenameValue] = useState(isFirstPayment ?  objectData.middleName : objectData.payerMiddleName);
    const [inputLastnameValue, setInputLastnameValue] = useState(isFirstPayment ? objectData.lastName : objectData.payerLastName);
    const [inputEmailValue, setInputEmailValue] = useState(objectData.email || '');
    const [inputZipcodeValue, setInputZipcodeValue] = useState(objectData.zipCode || '');
    const [inputAddressValue, setInputAddressValue] = useState(objectData.address || '');
    const [inputRelationshipValue, setInputRelationshipValue] = useState(objectData.relationship || '');
    const [inputPhoneNumberValue, setInputPhoneNumberValue] = useState(objectData.phone || '');
    const [selectedPayingForPolicyValue, setSelectedPayingForPolicyValue] = useState('');
    const [selectedCountryValue, setSelectedCountryValue] = useState(showPaymentMethodTitle ? 'BR' : (objectData.country || undefined));
    const [selectedStateValue, setSelectedStateValue] = useState(showPaymentMethodTitle ? '' : (objectData.state || ''));
    const [selectedCityValue, setSelectedCityValue] = useState(showPaymentMethodTitle ? '' : (getCityCodeByZone(objectData.country, objectData.state, objectData.city) || ''));
    const [optionsCountry, setOptionsCountry] = useState<{ value: string; label: string; }[]>([]);
    const [optionsState, setOptionsState] = useState<{ value: string; label: string; }[]>([]);
    const [optionsCity, setOptionsCity] = useState<{ value: string; label: string; }[]>([]);
    const [errorInputFirstname, setErrorInputFirstname] = useState('');
    const [errorInputMiddlename, setErrorInputMiddlename] = useState('');
    const [errorInputLastname, setErrorInputLastname] = useState('');
    const [errorInputEmail, setErrorInputEmail] = useState('');
    const [errorInputZipcode, setErrorInputZipcode] = useState('');
    const [errorInputAddress, setErrorInputAddress] = useState('');
    const [errorInputRelationship, setErrorInputRelationship] = useState('');
    const [errorInputPhoneNumber, setErrorInputPhoneNumber] = useState('');
    const [errorSelectedPayingForPolicy, setErrorSelectedPayingForPolicy] = useState('');
    const [errorSelectedCountry, setErrorSelectedCountry] = useState('');
    const [errorSelectedState, setErrorSelectedState] = useState('');
    const [errorSelectedCity, setErrorSelectedCity] = useState('');
    const [checked, setChecked] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const [submitRequested, setSubmitRequested] = useState(false);
    const [validationSettled, setValidationSettled] = useState(false);

    const clickButtonRef = useRef<() => void>(() => {});

    React.useImperativeHandle(ref, () => ({
        submitForm: () => clickButtonRef.current(),
    }));

    useEffect(() => {
        setSelectedPayingForPolicyValue("1");
        setOptionsCountry(countries?.map(country => ({ value: country.code.toUpperCase(), label: country.name })) || []);

        const countryCode = showPaymentMethodTitle ? 'BR' : objectData.country;
        const states = getStates(countryCode);
        setOptionsState(states.map(state => ({ value: state.code, label: state.name })));
        if (!showPaymentMethodTitle) {
            const cities = states.find(state => state.code === objectData.state)?.cities || [];
            setOptionsCity(cities.map(city => ({ value: city.code, label: camelize(city.name) })));
        }
    }, [objectData, showPaymentMethodTitle]);

    const function_firstname = (value:any) => {
        setInputFirstnameValue(value);
        if (isFirstPayment) {
            objectData.firstName = value;
        } else {
            objectData.payerFirstName = value;
        }
    };
    const function_middlename = (value:any) => {
        setInputMiddlenameValue(value);
        if (isFirstPayment) {
            objectData.middleName = value;
        } else {
            objectData.payerMiddleName = value;
        }
    };
    const function_lastname = (value:any) => {
        setInputLastnameValue(value);
        if (isFirstPayment) {
            objectData.lastName = value;
        } else {
            objectData.payerLastName = value;
        }
    };
    const function_email = (value:any) => {
        setInputEmailValue(value);
        objectData.email = value;
    }
    const function_phone = (value:any) => {
        setInputPhoneNumberValue(value);
        objectData.phone = value;
    }
    const function_zipcode = (value:any) => {
        setInputZipcodeValue(value);
        objectData.zipCode = value;
    }
    const function_billing_address = (value:any) => {
        setInputAddressValue(value);
        objectData.address = value;
    }
    const function_relationship = (value:any) => {
        setInputRelationshipValue(value);
        objectData.relationship = value;
    }

    const function_payingForPolicy = (value: any) => {
        setSelectedPayingForPolicyValue(value);
        objectData.payingForPolicy = value;
    }

    const getStates = (countryCurrent: string) => {
        const states = countries.find(country => country.code === countryCurrent)?.states || [];
        return states;
    }

    const camelize = (str:string) => {
        return str.toLowerCase().replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
    }

    const function_country = (value: any) => {
        setSelectedCountryValue(value);
        objectData.country = value;
        objectData.recipientCode = REACT_APP_FLYWIRE_CODE_OTHER;
        if (value === 'US') {
            objectData.recipientCode = REACT_APP_FLYWIRE_CODE_US
        }
        const states = getStates(value);
        setOptionsState(states.map(state => ({ value: state.code, label: state.name })));
        setOptionsCity([]);
        if (states.length === 1) {
            objectData.state = states[0].code;
            setSelectedStateValue(states[0].code);
            const cities = states[0].cities || [];
            setOptionsCity(cities.map(city => ({ value: city.code, label: camelize(city.name) })));
            if (cities.length === 1) {
                objectData.city = cities[0].code;
                objectData.cityForFlywire = cities[0].name;
                setSelectedCityValue(cities[0].code);
            }
        }
    }
    
    const function_state = (value: any) => {
        setSelectedStateValue(value);
        objectData.state = value;
        const states = getStates(selectedCountryValue);
        const cities = states.find(state => state.code === value)?.cities || [];
        setOptionsCity(cities.map(city => ({ value: city.code, label: camelize(city.name) })));
        objectData.city = '';
        objectData.cityForFlywire = '';
        if (cities.length === 1) {
            objectData.city = cities[0].code;
            objectData.cityForFlywire = cities[0].name;
            setSelectedCityValue(cities[0].code);
        } /*else if (cities.length > 1) {
            console.log('cities', cities);
            const selectedCityValue2 = cities.find(city => city.code === selectedCityValue)?.code || cities.find(city => city.name === selectedCityValue)?.code || '';
            console.log('selectedCityValue2', selectedCityValue2);
            setSelectedCityValue(selectedCityValue2);
            if (selectedCityValue2 !== '') {
                console.log('setting city for flywire', selectedCityValue2);
                objectData.city = selectedCityValue2;
                objectData.cityForFlywire = cities.find(city => city.code === selectedCityValue2)?.name || '';
            }
        }*/
    }
    const function_city = (value: any) => {
        setSelectedCityValue(value);
        objectData.city = value;
        objectData.cityForFlywire = optionsCity.find(city => city.value === value)?.label || optionsCity.find(city => city.label === value)?.label || '';
    }
    const recurrenceIsMandatory = isFirstPayment
        ? resolveRecurrence({
            market: REACT_APP_COUNTRY_DEPLOY,
            country: objectData.country || '',
            frequency: objectData.frequency,
            automaticApproval: objectData.automaticApproval,
            userAuthorized: checked,
            countryException: REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION,
            isFirstPaymentScreen: isFirstPayment,
        }).mandatory
        : (objectData.paymentFrequency === 'M' || objectData.paymentFrequency === 'Q');

    const handleCheckboxChange = (id: any) => {
        if (recurrenceIsMandatory) {
            return;
        }
        objectData.recurrent = !checked;
        setChecked(!checked);
    };
    const handleInputErrorFirstname = (error:any) => {
        setErrorInputFirstname(error);
    };
    const handleInputErrorMiddlename = (error:any) => {
        setErrorInputMiddlename(error);
    };
    const handleInputErrorLastname = (error:any) => {
        setErrorInputLastname(error);
    };
    const handleInputErrorEmail = (error:any) => {
        setErrorInputEmail(error);
    };
    const handleInputPhoneNumber = (error:any) => {
        setErrorInputPhoneNumber(error);
    }
    const handleInputErrorZipcode = (error:any) => {
        setErrorInputZipcode(error);
    };
    const handleInputErrorAddress = (error:any) => {
        setErrorInputAddress(error);
    };
    const handleInputErrorRelationship = (error:any) => {
        setErrorInputRelationship(error);
    };
    const handleSelectedPayingForPolicy = (error:any) => {
        setErrorSelectedPayingForPolicy(error);
    }
    const handleSelectedCountry = (error:any) => {
        setErrorSelectedCountry(error);
    }
    const handleSelectedState = (error:any) => {
        setErrorSelectedState(error);
    }
    const handleSelectedCity = (error:any) => {
        setErrorSelectedCity(error);
    }
    const errorValidationPayer = () => {
        return errorInputFirstname
            || errorInputLastname
            || errorInputEmail
            || errorInputPhoneNumber
            || errorSelectedPayingForPolicy;
    }

    const errorValidationPayment = () => {
        return errorInputZipcode
            || errorInputAddress
            || errorSelectedCountry
            || errorSelectedState
            || errorSelectedCity;
    }

    const errorValidation = () => errorValidationPayer() || errorValidationPayment();

    const validateFields = () => {
        if (!inputFirstnameValue || !inputLastnameValue || !inputEmailValue || !inputPhoneNumberValue || !inputAddressValue || !selectedCountryValue) {
            return false;
        }
        if (brokerLayout && !selectedStateValue) {
            return false;
        }
        if (brokerLayout && optionsCity.length > 0 && !selectedCityValue) {
            return false;
        }
        return true;
    }

    const click_button = () => {
        setFirstLoad(false);
        setValidationSettled(false);
        setSubmitRequested(true);
    }

    useEffect(() => {
        if (!submitRequested || firstLoad || validationSettled) return;
        setValidationSettled(true);
    }, [submitRequested, firstLoad, validationSettled]);

    useEffect(() => {
        if (!submitRequested || !validationSettled) return;
        setSubmitRequested(false);
        setValidationSettled(false);
        if (!validateFields() || errorValidation()) return;
        let cityTemp = optionsCity.find(city => city.value === selectedCityValue)?.label || '';
        objectData.cityForFlywire = camelize(cityTemp);
        if (optionsCity.length === 0 || objectData.cityForFlywire === '') {
            objectData.cityForFlywire = optionsState.find(state => state.value === selectedStateValue)?.label || '';
        }
        if (selectedPayingForPolicyValue === 'O' && errorInputRelationship) return;
        onClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitRequested, validationSettled]);

    // Keep the ref always pointing to the latest click_button closure
    clickButtonRef.current = click_button;

    const extLabel = (labelKey: string, required = true, override?: string) => brokerLayout ? (
        <p className="fp-field-label">
            {override ?? languageMessages(language, labelKey)}
            {required && <span className="fp-field-required"> *</span>}
        </p>
    ) : null;

    return (
        <div className="container">
            <div>
            <span className="payerTitle" style={brokerLayout ? {paddingBottom: '4px'} : (!errorValidationPayer() ? {paddingBottom: '12px'} : {paddingBottom: '2px'})}>{languageMessages(language, "payerInformation")}</span>
            <span className="validation" style={!errorValidationPayer() ? {display: 'none'} : {display: 'block'}}>{languageMessages(language, "validationFields")}</span>
            <div className="formData">
                    <div className="col col-12 col-sm-12">
                        {extLabel("payingForPolicy")}
                        <Dropdown
                            label={brokerLayout ? undefined : languageMessages(language, "payingForPolicy")}
                            placeholder={languageMessages(language, "selectOption")}
                            value={selectedPayingForPolicyValue}
                            onChange={function_payingForPolicy}
                            options={languageMessages(language, "payingForPolicyOptions")}
                            disabled={REACT_APP_COUNTRY_DEPLOY === 'BR'}
                            style={{ width: '100%' }}
                            //error={errorSelectedPayingForPolicy}
                            onError={handleSelectedPayingForPolicy}
                            language={language}
                        />
                    </div>
                    <div className={`col ${brokerLayout ? 'col-6 col-sm-12' : 'col-4 col-sm-6'}`}>
                        {extLabel("firstName")}
                        <InputText
                            label={brokerLayout ? undefined : languageMessages(language, "firstName")}
                            format="name"
                            placeholder={languageMessages(language, "firstName")}
                            value={inputFirstnameValue}
                            onChange={(e:any) => function_firstname(e.target.value)}
                            error={errorInputFirstname}
                            required={!firstLoad}
                            minLenght={!firstLoad ? 2 : 0}
                            maxLength={100}
                            onError={handleInputErrorFirstname}
                            language={language}
                        />
                    </div>
                    {!brokerLayout && <div className="col col-4 col-sm-6">
                        <InputText
                            label={undefined}
                            format="name"
                            placeholder={languageMessages(language, "middleName")}
                            value={inputMiddlenameValue}
                            onChange={(e:any) => function_middlename(e.target.value)}
                            error={errorInputMiddlename}
                            maxLength={100}
                            onError={handleInputErrorMiddlename}
                            language={language}
                        />
                    </div>}
                    <div className={`col ${brokerLayout ? 'col-6' : 'col-4'} col-sm-12`}>
                        {extLabel("lastName")}
                        <InputText
                            label={brokerLayout ? undefined : languageMessages(language, "lastName")}
                            format="name"
                            placeholder={languageMessages(language, "lastName")}
                            value={inputLastnameValue}
                            onChange={(e:any) => function_lastname(e.target.value)}
                            error={errorInputLastname}
                            required={!firstLoad}
                            minLenght={!firstLoad ? 2 : 0}
                            maxLength={100}
                            onError={handleInputErrorLastname}
                            language={language}
                        />
                    </div>
                    <div className="col col-6 col-sm-12">
                        {extLabel("email", true, brokerLayout ? "E-mail" : undefined)}
                        <InputText
                            label={brokerLayout ? undefined : languageMessages(language, "email")}
                            placeholder={languageMessages(language, "email")}
                            value={inputEmailValue}
                            onChange={(e:any) => function_email(e.target.value)}
                            error={errorInputEmail}
                            required={!firstLoad}
                            format="email"
                            maxLength={100}
                            onError={handleInputErrorEmail}
                            language={language}
                        />
                    </div>
                    <div className="col col-6 col-sm-12">
                        {extLabel("phone")}
                        <Phone
                            value={inputPhoneNumberValue}
                            language={language}
                            onChange={function_phone}
                            onError={handleInputPhoneNumber}
                            required={!firstLoad}
                        />
                    </div>
                    {showPaymentMethodTitle && (
                        <div className="col col-12">
                            <span className="payerTitle" style={brokerLayout ? {paddingBottom: '4px'} : (!errorValidationPayment() ? {paddingBottom: '12px'} : {paddingBottom: '2px'})}>{languageMessages(language, "paymentMethodTitle")}</span>
                            {brokerLayout && <span className="validation" style={!errorValidationPayment() ? {display: 'none'} : {display: 'block'}}>{languageMessages(language, "validationFields")}</span>}
                        </div>
                    )}
                    {showPaymentMethodTitle && (
                        <>
                            <div className={`col col-4 col-sm-6${brokerLayout ? ' order-sm-1' : ''}`}>
                                {extLabel("country")}
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "country")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedCountryValue}
                                    onChange={function_country}
                                    options={optionsCountry}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedCountry}
                                    language={language}
                                    filter={true}
                                    textFilter={languageMessages(language, "searchCountry")}
                                    required={!firstLoad}
                                    validation={true}
                                    disabled={!brokerLayout}
                                />
                            </div>
                            <div className={`col col-4 col-sm-6${brokerLayout ? ' order-sm-2' : ''}`}>
                                {extLabel("state")}
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "state")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedStateValue}
                                    onChange={function_state}
                                    options={optionsState}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedState}
                                    language={language}
                                    filter={true}
                                    textFilter={languageMessages(language, "searchState")}
                                    required={brokerLayout && !firstLoad}
                                    validation
                                />
                            </div>
                            <div className={`col col-4 col-sm-6${brokerLayout ? ' order-sm-3' : ''}`}>
                                {extLabel("city")}
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "city")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedCityValue}
                                    onChange={function_city}
                                    options={optionsCity}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedCity}
                                    language={language}
                                    required={brokerLayout && optionsCity.length > 0 && !firstLoad}
                                    validation
                                    filter={true}
                                    textFilter={languageMessages(language, "searchCity")}
                                    disabled={optionsCity.length === 0}
                                />
                            </div>
                            <div className={`col col-8 col-sm-12${brokerLayout ? ' order-sm-5' : ''}`}>
                                {extLabel("billingAddress")}
                                <InputText
                                    label={brokerLayout ? undefined : languageMessages(language, "billingAddress")}
                                    placeholder={languageMessages(language, "billingAddress")}
                                    value={inputAddressValue}
                                    onChange={(e:any) => function_billing_address(e.target.value)}
                                    error={errorInputAddress}
                                    required={!firstLoad}
                                    onError={handleInputErrorAddress}
                                    language={language}
                                    maxLength={100}
                                />
                            </div>
                            <div className={`col col-4 col-sm-6${brokerLayout ? ' order-sm-4' : ''}`}>
                                {extLabel("zipCode", false, zipCodeLabel)}
                                <InputText
                                    label={brokerLayout ? undefined : languageMessages(language, "zipCode")}
                                    placeholder={languageMessages(language, "zipCode")}
                                    value={inputZipcodeValue}
                                    onChange={(e:any) => function_zipcode(e.target.value)}
                                    error={errorInputZipcode}
                                    required={false}
                                    maxLength={10}
                                    onError={handleInputErrorZipcode}
                                    language={language}
                                />
                            </div>
                        </>
                    )}
                    {!showPaymentMethodTitle && (
                        <>
                            <div className="col col-8 col-sm-12 order-sm-1">
                                <InputText
                                    label={brokerLayout ? undefined : languageMessages(language, "billingAddress")}
                                    placeholder={languageMessages(language, "billingAddress")}
                                    value={inputAddressValue}
                                    onChange={(e:any) => function_billing_address(e.target.value)}
                                    error={errorInputAddress}
                                    required={!firstLoad}
                                    onError={handleInputErrorAddress}
                                    language={language}
                                    maxLength={100}
                                />
                            </div>
                            <div className="col col-4 col-sm-6 order-sm-2">
                                <InputText
                                    label={brokerLayout ? undefined : languageMessages(language, "zipCode")}
                                    placeholder={languageMessages(language, "zipCode")}
                                    value={inputZipcodeValue}
                                    onChange={(e:any) => function_zipcode(e.target.value)}
                                    error={errorInputZipcode}
                                    required={selectedCountryValue === 'US' && !firstLoad}
                                    maxLength={selectedCountryValue === 'US' ? 5 : 10}
                                    format={selectedCountryValue === 'US' ? "number" : undefined}
                                    minLenght={selectedCountryValue === 'US' ? 5 : 0}
                                    onError={handleInputErrorZipcode}
                                    language={language}
                                />
                            </div>
                            <div className="col col-4 col-sm-6 order-sm-3">
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "country")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedCountryValue}
                                    onChange={function_country}
                                    options={optionsCountry}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedCountry}
                                    language={language}
                                    filter={true}
                                    textFilter={languageMessages(language, "searchCountry")}
                                    required={!firstLoad}
                                    validation={true}
                                />
                            </div>
                            <div className="col col-4 col-sm-6 order-sm-4">
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "state")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedStateValue}
                                    onChange={function_state}
                                    options={optionsState}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedState}
                                    language={language}
                                    filter={true}
                                    textFilter={languageMessages(language, "searchState")}
                                    validation
                                />
                            </div>
                            <div className="col col-4 col-sm-6 order-sm-5">
                                <Dropdown
                                    label={brokerLayout ? undefined : languageMessages(language, "city")}
                                    placeholder={languageMessages(language, "selectOption")}
                                    value={selectedCityValue}
                                    onChange={function_city}
                                    options={optionsCity}
                                    style={{ width: '100%' }}
                                    onError={handleSelectedCity}
                                    language={language}
                                    required={false}
                                    filter={true}
                                    textFilter={languageMessages(language, "searchCity")}
                                    disabled={optionsCity.length === 0}
                                />
                            </div>
                        </>
                    )}
                    {selectedPayingForPolicyValue === '3' && <div className="col col-12 col-sm-12 order-sm-6">
                        <InputText
                            label={brokerLayout ? undefined : languageMessages(language, "relationship")}
                            placeholder={languageMessages(language, "relationship")}
                            value={inputRelationshipValue}
                            onChange={(e:any) => function_relationship(e.target.value)}
                            onError={handleInputErrorRelationship}
                            language={language}
                            maxLength={50}
                        />
                    </div>}
                    <div className="col col-12 order-sm-7" style = {showRecurrent ? {display: 'flex'} : {display: 'none'}}>
                        <OleCheckbox
                            id={123}
                            onChange={(handleCheckboxChange)}
                            checked={checked}
                            disabled={isFirstPayment && recurrenceIsMandatory}
                            text={languageMessages(language, "authorizeRecurrence")}
                            key={'authorizeRecurrence'}
                        />
                    </div>
                    <div className="col col-12 order-sm-8" style={hideButton ? { display: 'none' } : {}}>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                            <div className={'button'} onClick={click_button}>
                                <div className='text'><div>{buttonText}</div></div>
                            </div>
                        </div>
                        {REACT_APP_COUNTRY_DEPLOY === 'MX' && (
                            <div className="paymentCurrencyReminder">
                                <span dangerouslySetInnerHTML={{__html: languageMessages(language, "paymentCurrencyReminder").replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}} />
                            </div>
                        )}
                        {belowButtonContent}
                    </div>
                
            </div>
            </div>
        </div>
    );
});

FormPersonalData.displayName = 'FormPersonalData';

export default FormPersonalData;
